import { Controller } from "@hotwired/stimulus"

// Manages Web Push notification subscriptions.
// Attach with data-controller="push-notifications".
// Requires the PWA service worker (sw.js) to be registered.
//
// iOS Safari 16.4+ requires the app to be installed as a PWA (added to home screen)
// before push notifications are available.
export default class extends Controller {
  static targets = ["subscribeBtn", "unsubscribeBtn", "status"]
  static values  = { subscribed: Boolean }

  connect() {
    if (!this.pushSupported()) {
      this.setStatus("Push notifications are not supported in this browser.")
      return
    }
    this.checkSubscriptionState()
  }

  // ─── Public actions ────────────────────────────────────────────────────────

  async subscribe(event) {
    event?.preventDefault()
    if (!this.pushSupported()) return

    try {
      const registration = await this.getServiceWorkerRegistration()
      if (!registration) return

      const permission = await Notification.requestPermission()
      if (permission !== "granted") {
        this.setStatus("Notification permission denied.")
        return
      }

      const vapidKey = await this.fetchVapidPublicKey()
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidKey)
      })

      await this.sendSubscriptionToServer(subscription, "POST")
      this.subscribedValue = true
      this.setStatus("Push notifications enabled.")
    } catch (err) {
      console.error("[PushNotifications] subscribe error:", err)
      this.setStatus("Failed to enable push notifications.")
    }
  }

  async unsubscribe(event) {
    event?.preventDefault()
    if (!this.pushSupported()) return

    try {
      const registration = await this.getServiceWorkerRegistration()
      const subscription = await registration?.pushManager.getSubscription()

      if (subscription) {
        await this.sendSubscriptionToServer(subscription, "DELETE")
        await subscription.unsubscribe()
      }

      this.subscribedValue = false
      this.setStatus("Push notifications disabled.")
    } catch (err) {
      console.error("[PushNotifications] unsubscribe error:", err)
      this.setStatus("Failed to disable push notifications.")
    }
  }

  // ─── Value callbacks ───────────────────────────────────────────────────────

  subscribedValueChanged(value) {
    this.toggleButtons(value)
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  pushSupported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  async getServiceWorkerRegistration() {
    try {
      return await navigator.serviceWorker.ready
    } catch (e) {
      console.error("[PushNotifications] SW not ready:", e)
      return null
    }
  }

  async checkSubscriptionState() {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      this.subscribedValue = !!subscription
    } catch (_e) {
      this.subscribedValue = false
    }
  }

  async fetchVapidPublicKey() {
    const res = await fetch("/push_subscriptions/vapid_public_key")
    const data = await res.json()
    return data.vapid_public_key
  }

  async sendSubscriptionToServer(subscription, method) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""

    if (method === "DELETE") {
      return fetch("/push_subscriptions", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      })
    }

    return fetch("/push_subscriptions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ subscription: subscription.toJSON() })
    })
  }

  toggleButtons(subscribed) {
    if (this.hasSubscribeBtnTarget)   this.subscribeBtnTarget.classList.toggle("hidden", subscribed)
    if (this.hasUnsubscribeBtnTarget) this.unsubscribeBtnTarget.classList.toggle("hidden", !subscribed)
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  // Converts a URL-safe base64 VAPID public key to a Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64  = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = atob(base64)
    return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
  }
}
